import * as Sentry from '@sentry/browser';
import { Store as AlephBetStore, StoreItem as AlephBetStoreItem } from 'alephbet';
import { deleteUser, getAuth, sendEmailVerification, signOut } from 'firebase/auth';
import Vue from 'vue';
import { ApolloProvider } from 'vue-apollo';
import { ActionTree, MutationTree } from 'vuex';

import { STORAGE_MIGRATION_REVISION } from '@/migrate-local-storage';

import { ExperimentTypes } from '@/enums/experiment';
import { LoginProvider, LoginProviderAccount, LoginProviderByFirebaseProviderId } from '@/enums/login';
import { OnboardingStatus } from '@/enums/onboarding-status';
import { WebLocale } from '@/enums/web-locale';

import { getAxios } from '@/helpers/axios-helper';
import { CookiebotHelper } from '@/helpers/cookiebot-helper';

import IdHelper from '@/helpers/id-helper';
import OnboardingHelper from '@/helpers/onboarding-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { SettingsHelper } from '@/helpers/settings-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import UserHelper from '@/helpers/user-helper';
import {
	DataProcessingService,
	DataProcessingServiceConsent,
	LocalDataProcessingServices,
} from '@/helpers/usercentrics-helper';

import { AuthResponse } from '@/interfaces/responses/auth';
import { OfferMonetizationType, OfferPresentationType, TitleObjectType } from '@/interfaces/titles';

import { GettersConfig } from '@/interfaces/store';

import ToastHelper from '@/helpers/toast/toast-helper';
import { ReadyType } from '@/stores/ready.store';

import {
	GetNewDeviceIdInputDocument,
	GetNewDeviceIdInputMutation,
	GetNewDeviceIdInputMutationVariables,
} from '@/graphql/mutation/GetNewDeviceId.mutation';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { getVm } from '@/helpers/vm-helper';
import Cookies from 'js-cookie';

// Start COOKIE_BANNER_EXPERIMENT
import { NewPageType } from '@/@types/graphql-types';
import { findSubscription } from '@/helpers/pro-web-helper';
import { SubscriptionInfo } from '@/interfaces/subscriptions';
// End
const RECENT_SEARCH_TERMS_COUNT = 10;

export const CONSENT_PARTNERS = {
	google: { name: 'Google', short: 'G', id: '1' },
	facebook: { name: 'Facebook', short: 'FB', id: '2' },
	twitter: { name: 'Twitter', short: 'TW', id: '4' },
	tiktok: { name: 'TikTok', short: 'TT', id: '6' },
	allother: { name: 'All Other Providers', short: 'AO', id: '5' },
	emails: { name: 'Email', short: 'E', id: '7' },
	terms_and_conditions: { name: 'Terms and Conditions', short: 'TC', id: '9' },
} as const;

export type Consent = keyof typeof CONSENT_PARTNERS;

export interface UserLocation {
	latitude: number;
	longitude: number;
	name: string;
}

export interface ExperimentFromUrl {
	experiment: string;
	variant?: string;
	sendSnowplowEvent?: boolean;
}

export type UserExperiment = AlephBetStoreItem & { tracking: boolean; preferred: boolean };
export type UserExperiments = {
	[name: string]: UserExperiment;
};

export interface UserInfo {
	email?: string;
	loginProvider?: LoginProvider;
	name?: string;
	emailVerified?: boolean;
	creationTime?: Date;
}

export enum TasteSurveyType {
	NONE = 'NONE',
	DIVERGING_TITLES = 'DIVERGING_TITLES',
	FINDER_GAME = 'FINDER_GAME',
	TV_APP_BLANK = 'TV_APP_BLANK',
	AUTO_DIGGER = 'AUTO_DIGGER',
	STATIC_DIGGER = 'STATIC_DIGGER',
	STATIC_MAPPINGS = 'STATIC_MAPPINGS',
	GLOBAL_CLICKED_CTA = 'GLOBAL_CLICKED_CTA',
}

export enum VisibleFlyoutType {
	HOME_INVITATION_BOX = 'homeInvitationBox',
	ONBOARDED_HOME = 'onboardedHome',
	FREEMIUM_BANNER = 'freemiumBanner',
}

enum UserStatus {
	FREQUENT_USER = 'FREQUENT_USER',
	INFREQUENT_USER = 'INFREQUENT_USER',
	POWER_USER = 'POWER_USER',
}

export enum premiumPurchaseFlowState {
	AUTH_STARTED = 'AUTH_STARTED',
	AUTH_SUCCESS = 'AUTH_SUCCESS',
	CHECKOUT = 'CHECKOUT',
}

type UserStatusPlatform = 'mob' | 'total' | 'tv' | 'web';

export interface UserSettings {
	jw_consents: Record<Consent, boolean | null>;
	providers: string[];
	packages_provider_filter: OfferMonetizationType[];
	hidden_timeline_specials: string[];
	taste_survey_completed: boolean;
	taste_survey_type: TasteSurveyType;
	taste_survey_model_version: string;
	should_see_tv_app_banner?: boolean;
	sessions_per_platform?: { [p in UserStatusPlatform]: number };
	status_per_platform?: { [p in UserStatusPlatform]: UserStatus };
	uc_consents: { id: string; consent: boolean }[] | null;
	// applications: UserApplication[];
}

export interface UserSearches {
	recentSearchTerms: string[];
	recentlyVisitedTitleIds: string[];
}

export interface DiscoverySettings {
	continueWatchingHintDismissed: boolean;
}

// ExperimentSettings is used to define the `experiments` key in the local storage
export interface ExperimentSettings {
	// placeholderSetting: PlaceholderType;
}

export interface VisibleFlyouts {
	[VisibleFlyoutType.HOME_INVITATION_BOX]: boolean;
	[VisibleFlyoutType.ONBOARDED_HOME]: boolean;
	[VisibleFlyoutType.FREEMIUM_BANNER]: boolean;
	[flyoutName: string]: boolean;
}

export type UserState = {
	accessToken: string | undefined; // used to send for certain routes with authorization token
	alephbet: AlephBetStore;
	appReviewInquired: boolean;
	discoverySettings: DiscoverySettings;
	enableProvidersFilter: boolean;
	experiments: ExperimentSettings; // for A/B testing
	hasCancelledPremium: boolean;
	hideSeenDislikedTitles: boolean;
	info: UserInfo;
	isPackagesFirstTimeUser: boolean;
	isPremium: boolean;
	isVerificationEmailSent: boolean;
	jwId: string | undefined;
	jwLoginId: string | undefined;
	lastActiveNewPageType: NewPageType;
	lastUsedLoginProvider: string | undefined;
	location: UserLocation | undefined;
	loggedInProviders: LoginProvider[]; // list of provider names the user is authenticated with
	loginProgress: boolean;
	mobileBannerHidden: boolean;
	onboarding_status: OnboardingStatus | null;
	persistedOverriddenLanguage: string | null;
	persistedWebLocale: WebLocale | null;
	preferredExperiments: { [experiment: string]: { variant: string; sendSnowplowEvent: boolean } };
	preferences: {
		buyboxPresentationType: OfferPresentationType | null;
	};
	premiumPurchaseFlowState: premiumPurchaseFlowState | null;
	searches: UserSearches;
	seenSurveys: Record<string, string[]>;
	settings: UserSettings; // user settings for non- and logged-in users
	storageMigratedRevision: number; // used to determine when to do migration upon startup
	sharedListOnboardingSeen: boolean; // used to determine when lists onboarding was shown
	lastTimeListsOnboardingWasShownTS: number; // use to determine when onboarding was shown last time
	submittedPremiumFeedbackSurvey: boolean; // have sent the feedback to see why he didn't buy premium
	// Start COOKIE_BANNER_EXPERIMENT
	subscriptionInfo?: SubscriptionInfo;
	// End
	timeZone: string | undefined;
	usedTrailerRadarTitles: Record<string, string[]>; // e.g.: "cw_2019_15": ["tm5", "tm393"]
	visibleFlyouts: VisibleFlyouts;
	// we need a way to reactively store all the DPS (data processing services) from Usercentrics.
	// then we can build an eligible DPS list by combining Usercentrics DPS and our usercentrics-helper.ts DPS.
	// with this we won't break any web app releases by changing them.
	usercentricsDPS: { id: string; name: string }[] | null;
	webBrazeEnabled: boolean;
};

export interface UserGetters {
	allConsentPartnersAnswered: boolean;
	bestEffortId: string | undefined;
	buyboxPresentationType: UserState['preferences']['buyboxPresentationType'];
	consents: Record<Consent, boolean | null> | null; // deprecated in favor if DPSconsents
	dpsConsents: Record<DataProcessingService, boolean | null> | null; // data processing consents (usercentrics)
	experimentSettings: (type: keyof ExperimentSettings) => Partial<ExperimentTypes>;
	hasCancelledPremium: boolean;
	hasCompletedSurvey: boolean;
	isFlyoutVisible: (name: string) => boolean;
	isHideSeenDislikedTitlesEnabled: boolean;
	isLastUsedLoginProvider: (providerName?: string) => boolean;
	isLoggedIn: (provider?: LoginProvider) => boolean;
	isPremium: boolean;
	isPremiumFeedbackSurveySubmitted: boolean;
	isRedirectToHomeBannerVisible: boolean;
	isUserOnboarded: boolean;
	isWebBrazeEnabled: boolean;
	jwLoginId: string | undefined;
	lastActiveNewPageType: NewPageType;
	packagesProviderFilter: OfferMonetizationType[];
	settingsProvider: string[];
	subscriptionInfo: SubscriptionInfo | null;
	userExperiments: UserExperiments;
	userInfo: UserInfo;
	userSearches: UserState['searches'];
}
export type UserGettersConfig = GettersConfig<UserState, UserGetters>;

export const getInitialUserState = (): UserState => ({
	accessToken: undefined,
	alephbet: {},
	appReviewInquired: false,
	discoverySettings: {
		continueWatchingHintDismissed: false,
	} as DiscoverySettings,
	enableProvidersFilter: false,
	experiments: {},
	hasCancelledPremium: false,
	hideSeenDislikedTitles: false,
	info: {},
	isPackagesFirstTimeUser: true,
	isPremium: false,
	isVerificationEmailSent: false,
	jwId: undefined,
	jwLoginId: undefined,
	lastActiveNewPageType: NewPageType.New,
	lastUsedLoginProvider: undefined,
	location: undefined,
	loggedInProviders: [],
	loginProgress: false,
	mobileBannerHidden: false,
	onboarding_status: null,
	persistedOverriddenLanguage: null,
	persistedWebLocale: null,
	preferredExperiments: {},
	preferences: {
		buyboxPresentationType: null,
	},
	premiumPurchaseFlowState: null,
	searches: {
		recentSearchTerms: [],
		recentlyVisitedTitleIds: [],
	},
	seenSurveys: {},
	settings: {
		// fill create map with consent partners valued with `null`
		jw_consents: (Object.keys(CONSENT_PARTNERS) as Consent[]).reduce((root, key) => {
			root[key] = null;
			return root;
		}, {} as Record<Consent, boolean | null>),
		providers: [] as string[],
		hidden_timeline_specials: [] as string[],
		taste_survey_completed: false,
		taste_survey_type: TasteSurveyType.AUTO_DIGGER,
		taste_survey_model_version: '',
		uc_consents: null,
	} as UserSettings,
	sharedListOnboardingSeen: false,
	storageMigratedRevision: STORAGE_MIGRATION_REVISION,
	lastTimeListsOnboardingWasShownTS: 0,
	submittedPremiumFeedbackSurvey: false,
	timeZone: undefined,
	usedTrailerRadarTitles: {},
	usercentricsDPS: null,
	visibleFlyouts: {
		[VisibleFlyoutType.HOME_INVITATION_BOX]: true,
		[VisibleFlyoutType.ONBOARDED_HOME]: true as boolean,
		[VisibleFlyoutType.FREEMIUM_BANNER]: true,
	},
	webBrazeEnabled: false,
});

const defaultSubscriptionInfo: SubscriptionInfo = { id: '', priceId: '', price: 2.49, type: 'regular' };

export type UserStore = {
	state: UserState;
	getters: UserGettersConfig;
};

// it needs to be a function that will later be called.
const state = getInitialUserState;

// GETTERS
// -------
const getters: UserGettersConfig = {
	userExperiments: (state, getters) => {
		getters.isLoggedIn;
		const userExperiments = {} as UserExperiments;

		for (const name in state.preferredExperiments) {
			const { variant, sendSnowplowEvent: tracking } = state.preferredExperiments[name];
			userExperiments[name] = {
				in_sample: true,
				tracking,
				variant,
				preferred: true,
			};
		}

		for (const key in state.alephbet) {
			const [name, type] = key.split(':') as [string, keyof AlephBetStoreItem];
			if (!state.preferredExperiments[name]) {
				if (!userExperiments[name]) {
					userExperiments[name] = {
						tracking: true,
						preferred: false,
					} as UserExperiment;
				}
				(userExperiments[name][type] as AlephBetStoreItem[keyof AlephBetStoreItem]) = state.alephbet[key];
			}
		}
		return userExperiments;
	},
	userInfo: state => {
		return !state.accessToken ? {} : state.info;
	},
	buyboxPresentationType: state => state.preferences.buyboxPresentationType,
	isLoggedIn:
		state =>
		(provider?: LoginProvider): boolean => {
			if (provider) {
				return state.loggedInProviders.includes(provider);
			}
			return state.loggedInProviders.length > 0;
		},
	isLastUsedLoginProvider:
		state =>
		(providerName?: string): boolean => {
			return state.lastUsedLoginProvider === providerName;
		},
	isFlyoutVisible:
		state =>
		(name: string): boolean => {
			return state.visibleFlyouts[`${name}`];
		},
	bestEffortId: state => state.jwLoginId || state.jwId,
	jwLoginId: state => {
		// In some situations (purchases, JW Pro), we explicitly need a login ID to confirm
		// that the user has finished the auth flow and is logged in.
		return state.jwLoginId;
	},
	consents: state => {
		// TODO Store should produce computed consents. Right now we assume some consents could be missing (see ConsentModal.vue)
		return state.settings?.jw_consents || {};
	},
	dpsConsents: state => {
		const explicitlyConsented = (state.settings?.uc_consents || [])
			.filter(item => item.consent)
			.map(item => item.id);
		const excplicitlyRejected = (state.settings?.uc_consents || [])
			.filter(item => !item.consent)
			.map(item => item.id);

		return (Object.keys(LocalDataProcessingServices) as DataProcessingService[]).reduce((root, serviceKey) => {
			const service = LocalDataProcessingServices[serviceKey];
			let consentValue = null;
			if (explicitlyConsented.includes(service.id)) {
				// user HAS consented to this service
				consentValue = true;
			} else if (excplicitlyRejected.includes(service.id)) {
				// user HAS rejected consent to this service
				consentValue = false;
			} else {
				// user HAS NOT consented nor rejected to this service
				consentValue = null;
			}
			root![serviceKey] = consentValue;
			return root;
		}, {} as UserGetters['dpsConsents']);
	},

	userSearches: state => {
		return {
			recentSearchTerms: state.searches.recentSearchTerms.slice(0, 10),
			recentlyVisitedTitleIds: state.searches.recentlyVisitedTitleIds.slice(0, 10),
		};
	},
	hasCompletedSurvey: state => state.settings.taste_survey_completed || false,
	packagesProviderFilter: state => state.settings.packages_provider_filter || [OfferMonetizationType.FLATRATE],
	isRedirectToHomeBannerVisible: state => {
		const onboardingStatus = [
			OnboardingStatus.GLOBAL_CLICKED_CTA,
			OnboardingStatus.USER,
			OnboardingStatus.ONBOARDED,
		];

		return (
			!state.settings.taste_survey_completed &&
			state.settings.taste_survey_type !== TasteSurveyType.GLOBAL_CLICKED_CTA &&
			!onboardingStatus.includes(state.onboarding_status as OnboardingStatus)
		);
	},
	isUserOnboarded: state => {
		const status = [
			OnboardingStatus.ONBOARDED,
			OnboardingStatus.USER,
			OnboardingStatus.GLOBAL_CLICKED_CTA,
			OnboardingStatus.SIGNED_UP,
		];
		return state.onboarding_status != null && status.includes(state.onboarding_status);
	},
	isHideSeenDislikedTitlesEnabled: state => {
		return state.hideSeenDislikedTitles;
	},
	isPremium: state => {
		return state.isPremium;
	},

	hasCancelledPremium: state => {
		return state.hasCancelledPremium;
	},

	isPremiumFeedbackSurveySubmitted: state => {
		return state.submittedPremiumFeedbackSurvey;
	},
	/**
	 * experiment settings getter
	 * Usage:
	 *
	 *
	 * @param {ExperimentSettings} type key for experiment settings
	 *
	 * @returns {ExperimentTypes} settings for experiment key
	 */
	experimentSettings:
		state =>
		(type: keyof ExperimentSettings): Partial<ExperimentTypes> => {
			return state.experiments[type];
		},
	settingsProvider: state => {
		return state.settings.providers ?? [];
	},
	isWebBrazeEnabled: state => {
		return state.webBrazeEnabled;
	},
	// applicationsUsed: state => state.settings.applications || [],

	/**
	 * important getter to determine whether the consent banner is displayed.
	 *
	 * there are 2 differently maintained sets of data processsing services :
	 * - local: usercentrics-helper.ts UCDataProcessingServices
	 * - usercentrics: admin.usercentrics.eu > service settings > DPS
	 *
	 * in a perfect world, the 2 sets of consent partners are in sync.
	 * but we have to live with the fact that when we add a new DPS in UC first and then patch the consent partners later in the web app,
	 * the 2 sets are out of sync.
	 * that's why we need to make an AND link between them, so we know what consent partners to consider that are the same in both sets.
	 *
	 * note: without this we'd run into unclickable consent banners, because the length of consent partners are not the same,
	 *       and therefore allConsentPartnersAnswered will always return false.
	 */
	allConsentPartnersAnswered: (state, getter) => {
		if (state.usercentricsDPS === null) {
			// if Usercentrics lib hasn't loaded and initialised yet, we say the DPS haven't been answered
			return false;
		}
		const localDpsIds = (Object.keys(LocalDataProcessingServices) as DataProcessingService[]).map(
			key => LocalDataProcessingServices[key].id
		);
		const ucDpsIds = state.usercentricsDPS.map(dps => dps.id);
		const eligibleDps = (Object.keys(getter.dpsConsents || {}) as DataProcessingService[])
			.map(key => ({
				id: LocalDataProcessingServices[key].id,
				name: key,
				status: getter.dpsConsents![key],
			}))
			// filter out any DPS that aren't present in the local DPS
			.filter(dps => localDpsIds.includes(dps.id))
			// filter out any DPS that aren't present in the Usercentrics DPS
			.filter(dps => ucDpsIds.includes(dps.id));
		const explicitlyAnswered = eligibleDps.filter(dps => dps.status !== null); // explicit = true | false
		return explicitlyAnswered.length >= eligibleDps.length;
	},
	// Start COOKIE_BANNER_EXPERIMENT
	subscriptionInfo: state => {
		return state.subscriptionInfo || defaultSubscriptionInfo;
	},
	//End
	lastActiveNewPageType: state => state.lastActiveNewPageType,
};

// ACTIONS
// -------
const actions: ActionTree<UserState, any> = {
	applyExperimentFromUrl: async (
		{ dispatch },
		{
			add,
			remove,
		}: {
			add: ExperimentFromUrl;
			remove: ExperimentFromUrl;
		}
	) => {
		const promises: Promise<any>[] = [];
		add && promises.push(dispatch('addPreferredExperiment', add));
		remove && promises.push(dispatch('removePreferredExperiment', remove));
		return Promise.all(promises);
	},
	addPreferredExperiment: ({ commit, state }, { experiment, variant, sendSnowplowEvent }: ExperimentFromUrl) => {
		if (!variant) return;

		let preferredExperiments: UserState['preferredExperiments'] | null = null;
		const current = state.preferredExperiments[experiment];
		sendSnowplowEvent = sendSnowplowEvent || false;

		if (!current || current.variant !== variant || current.sendSnowplowEvent !== sendSnowplowEvent) {
			preferredExperiments = { ...state.preferredExperiments };
			preferredExperiments[experiment] = { variant, sendSnowplowEvent };
		}

		if (preferredExperiments) {
			commit('REMOVE_ALEPHBET_BY_NAME', experiment);
			commit('SET_PREFERRED_EXPERIMENTS', preferredExperiments);
		}
	},

	removePreferredExperiment: ({ commit, state }, { experiment }: ExperimentFromUrl) => {
		let preferredExperiments: UserState['preferredExperiments'] | null = null;
		if (state.preferredExperiments[experiment]) {
			preferredExperiments = { ...state.preferredExperiments };
			delete preferredExperiments[experiment];
		}

		if (preferredExperiments) {
			commit('REMOVE_ALEPHBET_BY_NAME', experiment);
			commit('SET_PREFERRED_EXPERIMENTS', preferredExperiments);
		}
	},

	fetchJwId: async ({ dispatch, state }, apolloProvider: ApolloProvider) => {
		try {
			const variables = await IdHelper.getIdServiceVariables(
				state.jwId || null,
				state.settings?.jw_consents || {}
			);

			const results = await apolloProvider.defaultClient.mutate<
				GetNewDeviceIdInputMutation,
				GetNewDeviceIdInputMutationVariables
			>({
				mutation: GetNewDeviceIdInputDocument,
				variables: {
					input: {
						doNotTrack: variables.dnt,
						ids: [variables.ids],
					},
				},
			});

			if (results?.data?.getNewDeviceId.deviceId) {
				dispatch('setJwId', results?.data?.getNewDeviceId.deviceId);
			} else {
				TrackingHelper.trackEvent('idservice', {
					action: 'lookup',
					label: 'failed',
					nonInteraction: true,
				});
				throw 'no valid ID received';
			}
		} catch (error: any) {
			// Webapp can init, but better keep tracking as this is still a limited app experience (no settings)
			captureMessageForSentry(
				'User Init Issue:',
				{ error, where: '[user.store.ts] fetchJwId' },
				Sentry.Severity.Error
			);
		}
	},

	setJwId: async ({ commit }, jwId: string) => {
		commit('SET_JW_ID', jwId);
		commit('ready/SET_READY', ReadyType.JW_ID, { root: true });
	},

	setBuyboxPresentationType(
		{ commit },
		payload: { presentationType: OfferPresentationType | null; additionalContexts?: SnowplowContext[] }
	) {
		const { presentationType, additionalContexts = [] } = payload;
		commit('SET_PREFERENCES_VALUE', presentationType);

		TrackingHelper.trackEvent(
			'presentationtype_change',
			{ action: presentationType ?? undefined },
			additionalContexts
		);
	},

	/**
	 * Finds out if is login or signup
	 *
	 * @throws Error when user is not signed up yet
	 */
	verifyRegistration: async (): Promise<void> => {
		const user = await UserHelper.currentUser();

		if (!user) return;
		const tokenResult = await user.getIdTokenResult();

		if (tokenResult.claims.registration_error) throw `registration error: ${tokenResult.claims.registration_error}`;
		if (!tokenResult.claims.jw_login_id) throw 'not yet registered internally';
	},

	refreshToken: async ({ commit, dispatch }): Promise<string | undefined> => {
		try {
			const token = await UserHelper.generateToken(false);

			if (!token) {
				throw new Error('Error getting refresh token');
			}

			commit('SET_AUTH_TOKEN', token);
			return token;
		} catch (error) {
			// Logout if we can't refresh the token.
			const err: any = error;
			const vm = getVm();

			try {
				await dispatch('logout');
			} catch (error) {
				console.error('[user.store.ts] refreshToken: Error logging out.');
			}

			captureMessageForSentry(
				'Refresh token Issue:',
				{ err, where: '[user.store.ts] refreshToken' },
				Sentry.Severity.Error
			);

			if (vm) {
				ToastHelper.error({
					message: vm.$t('WEBAPP_REFRESH_TOKEN_ERROR') as string,
					duration: 4000,
				});
			}
		}
	},

	registerInternally: async ({ commit, dispatch, state, getters, rootGetters }, isBackgroundTask: boolean) => {
		if (getters.isLoggedIn()) return;
		if (!isBackgroundTask) commit('SET_LOGIN_PROGRESS', true);

		const user = await UserHelper.currentUser();

		if (!user) {
			throw '[Firebase] internal registration failed: user not found';
		}

		// Get the firebase token, at this point we don't have the jw_login_id as part of claims
		const tokenResult = await user.getIdTokenResult(true);
		if (!tokenResult) {
			throw 'internal registration failed';
		}
		const { token } = tokenResult;

		try {
			await getAxios().post(
				`${JW_CONFIG.IDENTITY_URL}/register`,
				{},
				{
					headers: {
						Authorization: 'Bearer ' + token,
						// @ts-ignore
						['DEVICE-ID']: state.jwId,
					},
				}
			);

			// After registration, get the token result again, this should now contain the jw_login_id in claims.
			const newTokenResult = await user.getIdTokenResult(true);
			if (!newTokenResult || !newTokenResult.claims.jw_login_id) {
				throw 'internal registration failed';
			}

			await sendEmailVerification(user, {
				url: location.toString(),
			});
			commit('SET_AUTH_DATA', {
				connected_accounts: user.providerData.map((p: LoginProviderAccount) => {
					return {
						email: p!.email,
						login_provider: LoginProviderByFirebaseProviderId[p!.providerId],
					};
				}),
				device_id: state.jwId,
				login_id: newTokenResult.claims.jw_login_id,
				token: newTokenResult.token,
				loginProvider: LoginProviderByFirebaseProviderId[tokenResult.signInProvider as string],
			});

			await dispatch('loadSettings');
			await dispatch('saveSettings', { providers: rootGetters['constant/preselectedProviders'] });
			await dispatch('loadInfo');

			if (state.premiumPurchaseFlowState == premiumPurchaseFlowState.AUTH_STARTED) {
				// set state to continue purchase flow without user interaction
				commit('SET_PREMIUM_PURCHASE_FLOW_STATE', premiumPurchaseFlowState.AUTH_SUCCESS);
			}
			Vue.$jw.ready?.setReady(ReadyType.LOGIN_FINISHED);
		} catch (err) {
			throw err;
		} finally {
			commit('SET_LOGIN_PROGRESS', false);
		}
	},
	signInInternally: async ({ commit, dispatch, state }) => {
		commit('SET_LOGIN_PROGRESS', true);

		const user = await UserHelper.currentUser();

		if (!user) {
			commit('SET_LOGIN_PROGRESS', false);
			throw 'no firebase user';
		}
		try {
			const tokenResult = await user.getIdTokenResult(true);
			if (!tokenResult || !tokenResult.claims.jw_login_id) {
				throw 'need JW login ID for signing in internally';
			}

			await getAxios().post(
				`${JW_CONFIG.IDENTITY_URL}/sign_in`,
				{},
				{
					headers: {
						Authorization: 'Bearer ' + tokenResult.token,
						// @ts-ignore
						['DEVICE-ID']: state.jwId,
					},
				}
			);

			commit('SET_AUTH_DATA', {
				connected_accounts: user.providerData.map((p: LoginProviderAccount) => {
					return {
						email: p!.email,
						login_provider: LoginProviderByFirebaseProviderId[p!.providerId],
					};
				}),
				device_id: state.jwId,
				login_id: tokenResult.claims.jw_login_id,
				token: tokenResult.token,
				loginProvider: LoginProviderByFirebaseProviderId[tokenResult.signInProvider as string],
			});

			// update settings
			await dispatch('loadSettings');

			// only MOBILE
			const IapHelper = await (await import('@/helpers/iap-helper')).default;

			await IapHelper.updatePremiumStatus();
			await dispatch('loadInfo');
			// if sign-in/up was part of premium purchase flow
			if (state.premiumPurchaseFlowState == premiumPurchaseFlowState.AUTH_STARTED) {
				if (state.isPremium) {
					// user already has Premium, abort purchase flow
					commit('SET_PREMIUM_PURCHASE_FLOW_STATE', null);
				} else {
					// set state to continue purchase flow without user interaction
					commit('SET_PREMIUM_PURCHASE_FLOW_STATE', premiumPurchaseFlowState.AUTH_SUCCESS);
				}
			}

			Vue.$jw.ready?.setReady(ReadyType.LOGIN_FINISHED);
		} catch (err) {
			throw err;
		} finally {
			commit('SET_LOGIN_PROGRESS', false);
		}
	},

	/**
	 * Logout for all providers
	 */
	logout: async ({ dispatch, commit, state, rootState, rootGetters }, trackLogout = false) => {
		// Call signout to register in the consumer api that the user has signed out
		if (trackLogout) {
			await UserHelper.logout(state.accessToken, state.jwId);
		}

		try {
			await signOut(getAuth());
		} catch (err) {
			console.error('[Firebase] Signout error: ', err);
		}

		commit('LOGOUT');

		await dispatch('setPremiumStatus', false);
		// Start COOKIE_BANNER_EXPERIMENT
		await dispatch('setSubscriptionInfo', null);
		// End
		await dispatch('loadSettings');

		const locale = rootState.language.webLocale;
		const newOnboardingStatus = OnboardingHelper.getOnboardingStatus(
			locale,
			state.settings?.taste_survey_completed,
			false,
			rootState.routing.activeRoute?.path,
			state.settings?.taste_survey_type
		);
		// To reset onboarding status
		// Need to check if is loggedIn to avoid onboardingStatus reset
		// in case of failed login attempt (calls logout in that case)
		// update settings

		await dispatch('saveOnboardingStatus', newOnboardingStatus);
		await dispatch('saveSettings', { providers: rootGetters['constant/preselectedProviders'] });
	},

	sendCode: ({ state }, code: string) => {
		if (state.accessToken) {
			return UserHelper.sendCode(code, state.accessToken);
		}
	},

	hideMobileBanner({ commit }) {
		commit('SET_MOBILE_BANNER_HIDDEN', true);

		TrackingHelper.trackEvent('promotionbanner', {
			action: 'hide_click',
		});
	},

	// @deprecated: use saveDPSConsents for newer consent management
	saveConsents({ dispatch }, consents: Record<Consent, boolean>) {
		dispatch('saveSettings', { jw_consents: consents });
		TrackingHelper.trackConsent(consents);
		TrackingHelper.trackEvent('consent', {
			action: 'saved',
		});
		CookiebotHelper.saveConsents(consents);
		console.log('@todo replace with UsercentricsHelper.saveConsents(consents)');
	},

	updateAgreedToConsent({ commit }) {
		commit('SET_AGREED_TO_CONSENT');
	},
	/**
	 * we only locally save DPS consents that either:
	 * - have been answered explicitly
	 * - are true (usually being 'essential')
	 *
	 * note: it's possible that `usercentricsDpsConsents` has newer DPS that the local DPS (usercentrics-helper@LocalDataProcessingServices) don't have.
	 * in the getters `dpsConsents` and `allConsentPartnersAnswered` we ignore DPS (either from UC or local) that aren't present in both sets.
	 */
	saveDPSConsents({ dispatch, state }, usercentricsDpsConsents: DataProcessingServiceConsent[]) {
		const explicitConsents = usercentricsDpsConsents.filter(
			consent => consent.hasAnswered || consent.status === true
		);
		const uc_consents = explicitConsents.map(item => ({ id: item.id, consent: item.status }));
		// we want to prevent saving the uc_consents if they haven't changed.
		const getStringifiedConsent = (consents: UserSettings['uc_consents']) =>
			JSON.stringify([...(consents || [])].sort((a, b) => b.id.localeCompare(a.id)));
		if (getStringifiedConsent(state.settings.uc_consents) !== getStringifiedConsent(uc_consents)) {
			dispatch('saveSettings', { uc_consents });
		}
	},

	async loadSettings({ state, commit, rootGetters }) {
		await Vue.$jw.ready?.waitFor(ReadyType.JW_ID);

		if (!state.jwId) {
			return false;
		}
		const accessToken = state.accessToken;
		const locale = rootGetters['language/locale'];
		const settings = await SettingsHelper.getSettings(state.jwId, locale, accessToken);
		commit('SET_SETTINGS', settings);
		commit('ready/SET_READY', ReadyType.USER_SETTINGS_LOADED, { root: true });
	},

	async loadInfo({ state, commit }) {
		if (!state.accessToken) {
			commit('SET_USER_INFO', {});
			return;
		}

		const user = await UserHelper.currentUser();

		const info = {
			name: user?.displayName || '',
			email: user?.email || '',
			loginProvider: state.info.loginProvider,
			emailVerified: user?.emailVerified,
			creationTime: user?.metadata?.creationTime,
		};
		commit('SET_USER_INFO', info);
	},

	async deleteAccountPermanently({ dispatch }) {
		const user = await UserHelper.currentUser();
		if (!user) throw 'not logged in to Firebase';

		try {
			await deleteUser(user);
			await dispatch('logout');
		} catch (e) {
			const err: any = e;
			if (err.code !== 'auth/requires-recent-login') {
				captureMessageForSentry(
					'Error deleting user account:',
					{ error: err, where: '[user.store.ts] deleteAccountPermanently' },
					Sentry.Severity.Error
				);
			}
			throw e;
		}
	},

	async saveSettings({ commit, state, rootGetters }, partialSettings: Partial<UserSettings> = {}) {
		const locale = rootGetters['language/locale'];
		const settings = await SettingsHelper.mutateSettingsSet(
			state.jwId,
			state.accessToken,
			locale,
			state.settings,
			partialSettings
		);
		commit('SET_SETTINGS', settings);
	},

	saveOnboardingStatus({ commit }, status: OnboardingStatus) {
		commit('SAVE_ONBOARDING_STATUS', status);
	},

	savePersistedOverriddenLanguage({ commit }, language: string) {
		commit('SET_PERSISTED_OVERRIDDEN_LANGUAGE', language);
	},

	saveExperimentSettings({ commit }, expSettings: Partial<ExperimentSettings>) {
		commit('SET_EXPERIMENT_SETTINGS', expSettings);
	},

	saveSearchTerm({ commit }, searchTerm: string) {
		commit('SAVE_SEARCH_TERM', searchTerm);
	},
	saveVisitedTitleId({ commit }, visitedTitleId: string) {
		commit('SAVE_VISITED_TITLE_ID', visitedTitleId);
	},
	clearSearchHistory({ commit }) {
		commit('CLEAR_SEARCH_HISTORY');
	},
	hideFlyout({ commit }, flyoutName: VisibleFlyoutType | string) {
		commit('HIDE_FLYOUT', flyoutName);
	},
	async setPremiumStatus({ commit, dispatch, getters }, value: boolean) {
		if (!value && getters.isPremium) {
			await dispatch('filter/resetPremiumFilters', {}, { root: true });
			await dispatch('setHideSeenDislikedTitles', false);
		}
		commit('SET_IS_PREMIUM', value);
	},
	// Start COOKIE_BANNER_EXPERIMENT
	async setSubscriptionInfo({ commit }, subscriptionId: string) {
		const subscriptionInfo = findSubscription('id', subscriptionId);
		commit('SET_SUBSCRIPTION_INFO', subscriptionInfo);
	},
	// End
	setPremiumCancellationStatus({ commit }, value: boolean) {
		commit('SET_HAS_CANCELLED_PREMIUM', value);
	},
	setPremiumPurchaseFlowState({ commit }, value: premiumPurchaseFlowState | null) {
		commit('SET_PREMIUM_PURCHASE_FLOW_STATE', value);
	},
	setHideSeenDislikedTitles({ commit }, value: boolean) {
		commit('SET_HIDE_SEEN_DISLIKE_TITLES', value);
	},
	setTimeZone({ commit }, value: string) {
		commit('SET_TIME_ZONE', value);
	},
	setSubmittedPremiumFeedbackSurvey({ commit }, value: boolean) {
		commit('SET_SUBMITTED_PREMIUM_FEEDBACK_SURVEY', value);
	},
	setIsPackagesFirstTimeUser({ commit }) {
		commit('SET_IS_PACKAGES_FIRST_TIME_USER');
	},
	setIsVerificationEmailSent({ commit }, isVerificationEmailSent: boolean) {
		commit('SET_IS_VERIFICATION_EMAIL_SENT', isVerificationEmailSent);
	},
	setLastTimeListOnboardingWasShownTS({ commit }, timeStamp: number) {
		commit('SET_LAST_TIME_LIST_ONBOARDING_WAS_SHOWN_TS', timeStamp);
	},
	setWebBrazeEnabled: ({ commit }, isWebBrazeEnabled: boolean) => {
		commit('SET_WEB_BRAZE_ENABLED', isWebBrazeEnabled);
	},
	hideConsentOverlay: ({ commit }) => {
		commit('HIDE_CONSENT_OVERLAY');
	},
	setLastActiveNewPageType: ({ commit }, newPageCollectionType: NewPageType) => {
		commit('SET_LAST_ACTIVE_NEW_PAGE_TYPE', newPageCollectionType);
	},
	setSharedListOnboardingSeen({ commit }) {
		commit('SET_SHARED_LIST_ONBOARDING_SEEN');
	},
};

// MUTATIONS
// -------
const mutations: MutationTree<UserState> = {
	SET_LOCATION: (state, location: UserLocation) => {
		state.location = location;
	},

	SET_JW_ID: (state, jwId: string) => {
		Vue.set(state, 'jwId', jwId);
	},

	ADD_SEEN_SURVEY(
		state,
		{ surveyId, objectType, objectId }: { surveyId: number; objectType?: TitleObjectType; objectId?: number }
	) {
		if (!(surveyId in state.seenSurveys)) {
			state.seenSurveys[surveyId] = [];
		}
		if (objectType && objectId) {
			const id = `${objectType}-${objectId}`;
			if (state.seenSurveys[surveyId].indexOf(id) === -1) {
				state.seenSurveys[surveyId].push(id);
			}
		}
	},

	SET_PREFERENCES_VALUE(state, buyboxPresentationType: OfferPresentationType | null) {
		state.preferences.buyboxPresentationType = buyboxPresentationType;
	},

	SET_LOGIN_PROGRESS: (state, value: boolean) => {
		state.loginProgress = value;
	},

	SET_AUTH_DATA: (state, authData: AuthResponse) => {
		if (authData.connected_accounts) {
			const loggedInProviders: string[] = [];
			for (let i = 0; i < authData.connected_accounts.length; i++) {
				// @ts-ignore
				const { email, login_provider, is_entry_point } = authData.connected_accounts[i];

				if (is_entry_point) {
					Vue.set(state.info, 'email', email);
					Vue.set(state.info, 'loginProvider', login_provider);
				}
				loggedInProviders.push(login_provider);
			}
			Vue.set(state, 'loggedInProviders', loggedInProviders);
		} else {
			if (authData.connected_providers.length === 1) {
				Vue.set(state.info, 'loginProvider', authData.connected_providers[0]);
			}
			Vue.set(state, 'loggedInProviders', authData.connected_providers);
		}
		state.info.loginProvider = authData.loginProvider;

		// Cookies
		Cookies.remove('access_token');
		Cookies.set('access_token', authData.token, {
			expires: 365,
			secure: true,
			sameSite: 'strict',
		});

		Vue.set(state, 'jwLoginId', authData.login_id);
		Vue.set(state, 'accessToken', authData.token);
	},
	SET_AUTH_TOKEN: (state, token: string) => {
		// Cookies
		Cookies.remove('access_token');
		Cookies.set('access_token', token, {
			expires: 365,
			secure: true,
			sameSite: 'strict',
		});

		state.accessToken = token;
	},

	SET_USER_NAME(state, name) {
		Vue.set(state.info, 'name', name);
	},

	SET_USER_INFO(state, info) {
		Vue.set(state, 'info', info);
	},

	SET_SETTINGS(state, settings) {
		Vue.set(state, 'settings', settings);
	},

	LOGOUT: state => {
		// Cookies
		Cookies.remove('access_token');

		Vue.delete(state, 'jwLoginId');
		Vue.delete(state, 'accessToken');
		Vue.set(state, 'info', {});
		const lastLoginProvider: string = state.loggedInProviders[0] || 'justwatch';
		Vue.set(state, 'lastUsedLoginProvider', lastLoginProvider);
		Vue.set(state, 'loggedInProviders', []);
	},

	SET_MOBILE_BANNER_HIDDEN: (state, value: boolean) => {
		state.mobileBannerHidden = value;
	},

	HIDE_FLYOUT: (state, name: string) => {
		state.visibleFlyouts[`${name}`] = false;
	},

	SET_ALEPHBET(state, alephbet: any) {
		state.alephbet = alephbet;
	},

	SET_ALEPHBET_WITH_KEY_VALUE(state, { key, value }) {
		state.alephbet = { ...state.alephbet, [key]: value };
	},

	REMOVE_ALEPHBET_BY_NAME(state, name: string) {
		let alephbet = null;
		for (const key in state.alephbet) {
			const remove = key.includes(name);
			if (!alephbet && remove) {
				alephbet = { ...state.alephbet };
			}

			if (alephbet && remove) {
				delete alephbet[key];
			}
		}

		alephbet && Vue.set(state, 'alephbet', alephbet);
	},

	SET_PREFERRED_EXPERIMENTS(state, preferredExperiments: UserState['preferredExperiments']) {
		Vue.set(state, 'preferredExperiments', preferredExperiments);
	},

	// added for a/b test
	SET_EXPERIMENT_SETTINGS: (state: any, experiments) => {
		Vue.set(state, 'experiments', experiments);
	},

	SET_DISCOVERY_SETTINGS(state, discoverySettings: DiscoverySettings) {
		state.discoverySettings = discoverySettings;
	},

	SET_USED_TRAILER_RADAR_TITLES: (state, { cwKey, entityIds }: { cwKey: string; entityIds: string[] }) => {
		state.usedTrailerRadarTitles[cwKey] = entityIds;
	},
	SAVE_ONBOARDING_STATUS: (state, status) => {
		state.onboarding_status = status;
	},
	SET_PERSISTED_OVERRIDDEN_LANGUAGE: (state, language) => {
		state.persistedOverriddenLanguage = language;
	},
	SET_PERSISTED_WEB_LOCALE: (state, webLocale) => {
		state.persistedWebLocale = webLocale;
	},
	SAVE_SEARCH_TERM: (state, searchTerm) => {
		let { recentSearchTerms } = state.searches;
		if (!recentSearchTerms.includes(searchTerm)) {
			// recentSearchTerms = [searchTerm, ...recentSearchTerms];
			recentSearchTerms = [searchTerm].concat(recentSearchTerms);
		} else {
			const index = recentSearchTerms.findIndex(term => term === searchTerm);
			const term = recentSearchTerms[index];
			recentSearchTerms.splice(0, 0, term);
			recentSearchTerms.splice(index + 1, 1);
		}

		// limit stored search terms to 10 to avoid store explosion
		state.searches.recentSearchTerms = recentSearchTerms.slice(0, RECENT_SEARCH_TERMS_COUNT);
	},
	SAVE_VISITED_TITLE_ID: (state, visitedTitleId: string) => {
		let { recentlyVisitedTitleIds } = state.searches;
		const containsTitle: boolean = recentlyVisitedTitleIds.some((elem: string) => elem === visitedTitleId);

		if (!containsTitle) {
			recentlyVisitedTitleIds = [visitedTitleId].concat(recentlyVisitedTitleIds);
		} else {
			const index = recentlyVisitedTitleIds.findIndex(titleId => titleId === visitedTitleId);
			const title = recentlyVisitedTitleIds[index];
			recentlyVisitedTitleIds.splice(0, 0, title);
			recentlyVisitedTitleIds.splice(index + 1, 1);
		}

		// limit stored search terms to 10 to avoid store explosion
		state.searches.recentlyVisitedTitleIds = recentlyVisitedTitleIds.slice(0, RECENT_SEARCH_TERMS_COUNT);
	},
	CLEAR_SEARCH_HISTORY: state => {
		state.searches.recentSearchTerms = [];
		state.searches.recentlyVisitedTitleIds = [];
	},
	SET_ENABLE_PROVIDER_FILTER: (state, value: boolean) => {
		state.enableProvidersFilter = value;
	},
	SET_IS_PREMIUM: (state, value: boolean) => {
		state.isPremium = value;
	},
	SET_SUBSCRIPTION_INFO: (state, value: SubscriptionInfo) => {
		state.subscriptionInfo = value;
	},
	SET_HAS_CANCELLED_PREMIUM: (state, value: boolean) => {
		state.hasCancelledPremium = value;
	},
	SET_PREMIUM_PURCHASE_FLOW_STATE: (state, value: premiumPurchaseFlowState | null) => {
		state.premiumPurchaseFlowState = value;
	},
	SET_TIME_ZONE: (state, value: string) => {
		Vue.set(state, 'timeZone', value);
	},
	SET_HIDE_SEEN_DISLIKE_TITLES: (state, value: boolean) => {
		state.hideSeenDislikedTitles = value;
	},
	SET_SUBMITTED_PREMIUM_FEEDBACK_SURVEY: (state, value: boolean) => {
		state.submittedPremiumFeedbackSurvey = value;
	},
	SET_PACKAGES_PROVIDER_FILTER: (state, value: OfferMonetizationType[]) => {
		state.settings.packages_provider_filter = value;
	},
	SET_IS_PACKAGES_FIRST_TIME_USER: state => {
		state.isPackagesFirstTimeUser = false;
	},
	SET_IS_VERIFICATION_EMAIL_SENT: (state, isVerificationEmailSent: boolean) => {
		state.isVerificationEmailSent = isVerificationEmailSent;
	},
	SET_LAST_TIME_LIST_ONBOARDING_WAS_SHOWN_TS: (state, timeStamp: number) => {
		state.lastTimeListsOnboardingWasShownTS = timeStamp;
	},
	SET_WEB_BRAZE_ENABLED: (state, isWebBrazeEnabled: boolean) => {
		state.webBrazeEnabled = isWebBrazeEnabled;
	},
	SET_USERCENTRICS_DPS: (state, dataProcessingServices: { id: string; name: string }[]) => {
		state.usercentricsDPS = dataProcessingServices;
	},
	SET_LAST_ACTIVE_NEW_PAGE_TYPE: (state, newPageCollectionType: NewPageType) => {
		state.lastActiveNewPageType = newPageCollectionType;
	},
	SET_SHARED_LIST_ONBOARDING_SEEN: state => {
		state.sharedListOnboardingSeen = true;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
